import React from "react";
import Default_WhatsApp_background from "../../../Assets/Default_WhatsApp_background.jpeg";

interface MediaConfig {
  file_name?: string;
  file_type?: string;
  binary_data?: string;
  file_length?: string;
}

interface Props {
  content: string;
  mediaConfig?: MediaConfig | null;
}

interface TemplateComponent {
  text?: string;
  type: "HEADER" | "BODY" | "FOOTER" | "BUTTONS";
  format?: "TEXT" | "IMAGE" | "VIDEO" | "DOCUMENT";
  example?: {
    header_handle?: string[];
  };
  buttons?: Array<{
    type: string;
    text: string;
    url?: string;
    phone_number?: string;
  }>;
}

const CreateContentWhatsappCard: React.FC<Props> = ({ content, mediaConfig }) => {
  // Parse the template content
  const parseTemplateContent = (content: string) => {
    try {
      const parsed = JSON.parse(content);
      if (Array.isArray(parsed)) {
        return parsed as TemplateComponent[];
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  // Helper to get base64 source with proper mime type
  const getBase64Src = (base64: string, type: string) => {
    if (!base64) return null;

    // If base64 already has data URI prefix, return as is
    if (base64.startsWith('data:')) {
      return base64;
    }

    // Otherwise, add the appropriate mime type prefix
    let mimeType = '';
    if (type === 'image') {
      mimeType = mediaConfig?.file_type || 'image/jpeg';
    } else if (type === 'video') {
      mimeType = mediaConfig?.file_type || 'video/mp4';
    } else if (type === 'document') {
      mimeType = 'application/pdf';
    }

    return `data:${mimeType};base64,${base64}`;
  };

  // Helper to render formatted text with bold and italic
  const renderFormattedText = (text: string) => {
    if (!text) return "";

    let formatted = text;
    // Bold: *text* -> <strong>text</strong>
    formatted = formatted.replace(/\*([^*]+)\*/g, "<strong>$1</strong>");
    // Italic: _text_ -> <em>text</em>
    formatted = formatted.replace(/_([^_]+)_/g, "<em>$1</em>");

    return formatted;
  };

  const components = parseTemplateContent(content);

  const headerComponent = components.find((c) => c.type === "HEADER");
  const bodyComponent = components.find((c) => c.type === "BODY");
  const footerComponent = components.find((c) => c.type === "FOOTER");
  const buttonsComponent = components.find((c) => c.type === "BUTTONS");

  const noContentSelected = components.length === 0;

  const selectedOption = headerComponent?.format?.toLowerCase();
  const base64 = mediaConfig?.binary_data || null;
  const textInput = headerComponent?.format === "TEXT" ? headerComponent.text : null;
  const bodyText = bodyComponent?.text || "";
  const footerText = footerComponent?.text || "";
  const rows = buttonsComponent?.buttons || [];

  const isBodyEmpty = !bodyText;
  const isFooterEmpty = !footerText;

  const whatsappStyle = !noContentSelected
    ? {
      backgroundImage: `url(${Default_WhatsApp_background})`,
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundColor: "#f0f0f0"
    }
    : {
      backgroundColor: "#e5e7eb"
    };

  return (
    <div className="w-full max-w-[500px] border rounded-lg p-3 min-h-0 flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-bold">Content Preview</h2>
        {/* <div className="flex items-center gap-2 text-xs text-gray-400">
      <span>{bodyText.length} chars</span>
      <span title="WhatsApp templates are non-editable" className="cursor-default">🔒</span>
    </div> */}
      </div>
      <div
        className="flex rounded-[20px] flex-col justify-between w-full bottom-0 p-3 overflow-hidden"
        style={{ ...whatsappStyle, minHeight: "400px" }}
      >
        {noContentSelected ? (
          <div className="flex w-full h-[200px] items-center justify-center">
            <div className="text-center">
              <div className="text-6xl mb-4">📱</div>
              <div className="mt-6">
                <p className="text-xl font-semibold">Mobile screen</p>
              </div>
              <div className="w-[125px] mt-2" style={{ fontWeight: 500, fontSize: "14px" }}>
                <p>Preview varies based on platform selection</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Header Media */}
            <div className="flex justify-center">
              {selectedOption === "image" && base64 && (
                <div className="w-full">
                  <img
                    src={getBase64Src(base64, "image") || ""}
                    alt="Header Image"
                    className="w-full h-40 mt-2 object-cover mx-auto rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              )}
              {selectedOption === "video" && base64 && (
                <div className="w-full">
                  <video
                    controls
                    className="w-full h-[200px] object-cover mx-auto rounded-lg"
                    src={getBase64Src(base64, "video") || ""}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}
              {selectedOption === "document" && base64 && (
                <div className="w-full">
                  <div className="mb-2 p-3 bg-gray-100 rounded-lg flex items-center space-x-2">
                    <div className="w-6 h-6 text-red-500 text-2xl">📄</div>
                    <div>
                      <div className="text-sm font-medium">
                        {mediaConfig?.file_name || "Document.pdf"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {mediaConfig?.file_length ? `${(parseInt(mediaConfig.file_length) / 1024).toFixed(1)} KB` : "PDF Document"}
                      </div>
                    </div>
                  </div>
                  <embed
                    src={getBase64Src(base64, "document") || ""}
                    type="application/pdf"
                    width="100%"
                    height="200px"
                    className="rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              )}
              {(selectedOption === "image" || selectedOption === "video" || selectedOption === "document") && !base64 && (
                <div className="text-gray-400 text-center w-full h-[200px] flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
                  <div>
                    <div className="mb-2">
                      {selectedOption === "image" && "📷"}
                      {selectedOption === "video" && "🎥"}
                      {selectedOption === "document" && "📄"}
                    </div>
                    <div>No {selectedOption} uploaded</div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex-1 text-black w-full text-left">
              {selectedOption === "text" && textInput && (
                <p className="w-full text-base font-sans p-2 font-bold" style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
                  {textInput}
                </p>
              )}
              {!isBodyEmpty && (
                <div
                  className="mb-2 whitespace-pre-wrap break-words text-sm leading-relaxed p-2"
                  dangerouslySetInnerHTML={{ __html: renderFormattedText(bodyText) }}
                />
              )}
            </div>
            {!isFooterEmpty && (
              <div className="border-t mt-2 pt-2 text-center">
                <p className="font-semibold text-black" style={{ fontSize: "14px" }}>{footerText}</p>
              </div>
            )}
            {rows.length > 0 && (
              <div className="border-t mt-2 pt-2 pb-2 text-center space-y-1">
                {rows.map((row, index) => (
                  <div key={index} className="border-b border-gray-200 last:border-b-0 pb-1">
                    <p className="font-serif text-blue-400" style={{ fontSize: "14px" }}>
                      {row.type === "URL" ? (
                        <a href={row.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                          {row.text}
                        </a>
                      ) : (
                        row.text
                      )}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CreateContentWhatsappCard;